import { createAsyncThunk } from "@reduxjs/toolkit"
import { AppThunkAPIType } from "../../../app/appTypes"
import { DocumentKind } from "../documentsApi"
import { getWithAuth, postWithAuth } from "../../../http"

export enum ImportCounterpartyModeEnum {
    None = 0,
    Match = 1,
    Existing = 2,
    New = 3
}

export interface ImportCounterpartyRequestDto {
    mode: ImportCounterpartyModeEnum;
    publicId?: string | null;
    displayName: string;
    countryCode: string;
}

export interface ImportedDocument {
    id: number;
    number: string;
    title: string;
    type: string;
    version: number;
    isImported: boolean;
    structureId: string;
    language: string;
    documentKind: DocumentKind;
    companyId: number;
    createDate: Date;
    relatedCompanyName: string;
    guid: string;
    status: string;
    createdBy: string;
    createdByFullName: string;
    elements: ImportedDocumentElement[];
    counterparty: DocumentCounterpartyHeader;
}

export interface DocumentCounterpartyHeader {
    publicId: string;
    displayName: string;
    countryCode: string;
    accountManagerFullName: string;
}

export interface ImportedDocumentElement {
    id: number;
    order: number;
    elementType: string;
    contentText?: string | null;
    contentJson: string;
    styleJson?: string | null;
    pageNumber?: number | null;
    confidence?: number | null;
}

export interface ImportedDocumentNoteSuggestion {
    id: number;
    sessionId: string;
    targetDocumentId: number;
    targetBlockKey: string;
    targetImportedDocumentElementId?: number | null;
    sourceDocumentId: number;
    sourceDocumentTitle: string;
    sourceDocumentNo: string;
    sourceDocumentVersion: number;
    sourceBlockKey: string;
    sourceImportedDocumentElementId?: number | null;
    sourceImportedDocumentNoteId: number;
    sourceRatingCreatedBy: string;
    /** When the source note the suggestion was copied from was given. */
    sourceRatingDate?: string | null;
    sourceCustomerName?: string;
    sourceCountryCode?: string;
    sourceStructureId?: number | null;
    suggestedNote: string;
    suggestedComment: string;
    similarityScore: number;
    rank: number;
    status: string;
    reason: string;
    createdBy: string;
    createDate: string;
}

export interface ImportedDocumentBlockSuggestions {
    targetBlockKey: string;
    targetImportedDocumentElementId?: number | null;
    suggestions: ImportedDocumentNoteSuggestion[];
}

export const loadAllImportedDocuments = createAsyncThunk<
    ImportedDocument[],
    void,
    AppThunkAPIType
>("imported-documents/loadAll",
    async (_, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/imported-documents` }));
        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as ImportedDocument[];
        }

        return rejectWithValue(payload ?? { kind: "unknown" });
    }
);

export const loadImportedDocumentById = createAsyncThunk<
    ImportedDocument,
    number,
    AppThunkAPIType
>("imported-documents/loadById",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/imported-documents/${id}` }));
        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as ImportedDocument;
        }

        return rejectWithValue(payload ?? { kind: "unknown" });
    }
);

export const loadImportedDocumentSuggestions = createAsyncThunk<
    ImportedDocumentBlockSuggestions[],
    number,
    AppThunkAPIType
>("imported-documents/loadSuggestions",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/imported-documents/${id}/suggestions` }));
        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as ImportedDocumentBlockSuggestions[];
        }

        return rejectWithValue(payload ?? { kind: "unknown" });
    }
);

export const importDocument = createAsyncThunk<
    ImportedDocument,
    Omit<ImportedDocument, "id" | "isImported" | "companyId" | "guid" | "counterparty"> & Partial<Pick<ImportedDocument, "id">> & {
        counterpartyRequest: ImportCounterpartyRequestDto;
        customDeviceStructureNodeId?: string | null;
    },
    AppThunkAPIType
>("imported-documents/import", async (dto, { dispatch, rejectWithValue }) => {
    const result = await dispatch(
        postWithAuth({
            url: `api/imported-documents/import`,
            payload: dto
        })
    );

    const { payload } = result;

    if (postWithAuth.fulfilled.match(result)) {
        return payload as ImportedDocument;
    }

    return rejectWithValue(payload ?? { kind: "unknown" });
});

export const runImportedDocumentAiAnalysis = createAsyncThunk<
    void,
    number,
    AppThunkAPIType
>(
    "imported-documents/runAiAnalysis",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(
            postWithAuth({
                url: `api/imported-documents/${id}/run-ai-analysis`,
                payload: {}
            })
        );

        const { payload } = result;

        if (postWithAuth.fulfilled.match(result)) {
            return;
        }

        return rejectWithValue(payload ?? { kind: "unknown" });
    }
);
